import { useState } from 'react';
import { Layers, ChevronsUpDown, X, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type TransactionGroupById = 'project' | 'transactionType' | 'period' | 'costCentre' | 'employee';

export interface TransactionGroupByOption {
  id: TransactionGroupById;
  label: string;
}

export const TRANSACTION_GROUP_BY_OPTIONS: TransactionGroupByOption[] = [
  { id: 'project',         label: 'Project' },
  { id: 'transactionType', label: 'Transaction Type' },
  { id: 'period',          label: 'Period' },
  { id: 'costCentre',      label: 'Cost Centre' },
  { id: 'employee',        label: 'Employee' },
];

interface TransactionGroupByFilterProps {
  groupBy: TransactionGroupById | null;
  onGroupByChange: (value: TransactionGroupById | null) => void;
}

export function TransactionGroupByFilter({ groupBy, onGroupByChange }: TransactionGroupByFilterProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filteredOptions = TRANSACTION_GROUP_BY_OPTIONS.filter(
    (opt) => !search || opt.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-background text-sm',
            'hover:bg-muted/40 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
            groupBy !== null && 'border-primary/50 bg-primary/5'
          )}
        >
          <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className={groupBy === null ? 'text-muted-foreground' : 'text-foreground'}>
            {groupBy === null
              ? 'Group By'
              : TRANSACTION_GROUP_BY_OPTIONS.find((o) => o.id === groupBy)?.label}
          </span>
          {groupBy !== null ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onGroupByChange(null); }}
              className="ml-1"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          ) : (
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent className="p-0 w-60" align="start" side="bottom">
        {/* Search */}
        <div className="flex items-center border-b px-3 gap-2">
          <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search groupings..."
            className="flex-1 py-2.5 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')}>
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>

        {/* Options — single select */}
        <div className="py-1 max-h-64 overflow-y-auto">
          {filteredOptions.map((opt) => {
            const selected = groupBy === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  onGroupByChange(selected ? null : opt.id);
                  setOpen(false);
                }}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 w-full text-left cursor-pointer select-none',
                  'hover:bg-muted/60 transition-colors text-sm',
                  selected && 'bg-primary/5 text-primary font-medium'
                )}
              >
                {opt.label}
              </button>
            );
          })}
          {filteredOptions.length === 0 && (
            <p className="text-xs text-muted-foreground px-3 py-2">No items match.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
